/**
 * ArtifactRenderer — type-dispatcher for Workspace artifacts.
 *
 * Picks the per-type renderer based on `artifact.type`, wraps the consumer's
 * `onAction` callback with default handlers for the two content-tool actions
 * the contract guarantees the frontend can satisfy locally:
 *
 *   - `copy`     → write the artifact's body/text to the clipboard.
 *   - `download` → build a Blob and trigger a synthetic <a download> click.
 *
 * Anything else bubbles to the parent's `onAction` prop unchanged. If the
 * parent doesn't handle it either, the action is a no-op (logged).
 *
 * Type fallback: any artifact whose `type` isn't covered by the switch below
 * (today only `unknown`, plus future variants) routes to
 * `UnknownArtifactRenderer`.
 *
 * Spec: `.workspace-spec/WORKSPACE_CONTRACT.md`
 */
import { useCallback } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type {
  ActionReceiptPayload,
  Artifact,
  AskUserPayload,
  DiagramPayload,
  DocumentPayload,
  EmailDraftPayload,
  ImagePayload,
  OfficeFilePayload,
  PendingActionPayload,
  PlanPayload,
  ShortAnswerPayload,
  TablePayload,
  UnknownPayload,
} from "@/api/models"

import { logger } from "@/lib/logger"

import { ActionReceiptRenderer } from "./ActionReceiptRenderer"
import { AskUserRenderer } from "./AskUserRenderer"
import { DiagramRenderer } from "./DiagramRenderer"
import { DocumentRenderer } from "./DocumentRenderer"
import { EmailDraftRenderer } from "./EmailDraftRenderer"
import { ImageRenderer } from "./ImageRenderer"
import { OfficeFileRenderer } from "./OfficeFileRenderer"
import { PendingActionRenderer } from "./PendingActionRenderer"
import { PlanRenderer } from "./PlanRenderer"
import { ShortAnswerRenderer } from "./ShortAnswerRenderer"
import { TableRenderer } from "./TableRenderer"
import { UnknownArtifactRenderer } from "./UnknownArtifactRenderer"

export interface ArtifactRendererProps {
  artifact: Artifact
  versionChain?: Artifact[]
  /** Bubble-up dispatcher for actions this component doesn't intercept.
   *  The optional 3rd arg is a contract extension used by:
   *    - `plan.reject`        → `{ feedback }`   (Phase 1 workspace-focus)
   *    - `ask_user.respond`   → `{ answers }`    (Phase 2 workspace-focus)
   */
  onAction?: (
    actionId: string,
    artifact: Artifact,
    extra?: {
      feedback?: string
      answers?: Record<string, string | string[]>
    }
  ) => void
  onSelectVersion?: (artifactId: string) => void
  onViewTrace?: (missionId: string) => void
  onShowHistory?: () => void
  citationSection?: string
  /**
   * AI Explorer session-type namespace used by the `<PlanRenderer>` to
   * subscribe to `planProposed` / `planApproved` and render the inline
   * approval controls. Must be set in workspace mode; safe to omit in
   * legacy rendering or unit tests (the plan renderer falls back to its
   * read-only summary).
   */
  sessionType?: string
}

// ── Built-in action handlers ────────────────────────────────────────────────

/** Pull a sensible "body string" out of an artifact payload for copy/download.
 *  Returns `null` when the artifact doesn't have an obvious text representation
 *  (e.g. an image, an action receipt) — callers should bubble to the parent. */
function extractBodyText(artifact: Artifact): string | null {
  const payload = artifact.payload
  switch (artifact.type) {
    case "document":
      return (payload as DocumentPayload).body || null
    case "short_answer":
      return (payload as ShortAnswerPayload).body || null
    case "email_draft": {
      const p = payload as EmailDraftPayload
      const lines: string[] = []
      if (p.subject) lines.push(`Subject: ${p.subject}`)
      if (p.to && p.to.length) lines.push(`To: ${p.to.join(", ")}`)
      if (p.cc && p.cc.length) lines.push(`Cc: ${p.cc.join(", ")}`)
      if (p.bcc && p.bcc.length) lines.push(`Bcc: ${p.bcc.join(", ")}`)
      lines.push("")
      lines.push(p.body)
      return lines.join("\n")
    }
    case "table":
    case "list":
      return (payload as TablePayload).body || null
    case "diagram":
      return (payload as DiagramPayload).source || null
    case "image":
      return (payload as ImagePayload).url || null
    case "unknown":
      return (payload as UnknownPayload).description || null
    default:
      return null
  }
}

/** Suggest a download filename + mime type for the artifact. */
function suggestDownload(artifact: Artifact): {
  filename: string
  mime: string
  body: string
} | null {
  const titleSlug = (artifact.title || "artifact")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 60)

  switch (artifact.type) {
    case "document": {
      const p = artifact.payload as DocumentPayload
      return { filename: `${titleSlug}.md`, mime: "text/markdown", body: p.body }
    }
    case "short_answer": {
      const p = artifact.payload as ShortAnswerPayload
      return { filename: `${titleSlug}.md`, mime: "text/markdown", body: p.body }
    }
    case "email_draft": {
      const text = extractBodyText(artifact) || ""
      return { filename: `${titleSlug}.eml.txt`, mime: "text/plain", body: text }
    }
    case "table":
    case "list": {
      const p = artifact.payload as TablePayload
      if (p.format === "structured" && p.columns && p.rows) {
        // Lightweight CSV — escape double-quotes and wrap any cell containing
        // commas or newlines. Adequate for "save the comparison as csv"; not a
        // full RFC-4180 implementation.
        const columns = p.columns
        const head = columns.map((c) => csvEscape(c.label)).join(",")
        const body = p.rows
          .map((row) => columns.map((c) => csvEscape(stringifyCell(row[c.key]))).join(","))
          .join("\n")
        return {
          filename: `${titleSlug}.csv`,
          mime: "text/csv",
          body: `${head}\n${body}\n`,
        }
      }
      return { filename: `${titleSlug}.md`, mime: "text/markdown", body: p.body || "" }
    }
    case "diagram": {
      const p = artifact.payload as DiagramPayload
      let ext = "mmd"
      if (p.format === "svg") ext = "svg"
      else if (p.format === "graphviz") ext = "dot"
      return { filename: `${titleSlug}.${ext}`, mime: "text/plain", body: p.source }
    }
    default:
      return null
  }
}

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function stringifyCell(value: unknown): string {
  if (value == null) return ""
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

async function performCopy(artifact: Artifact): Promise<boolean> {
  const text = extractBodyText(artifact)
  if (text == null) {
    logger.warn(
      `ArtifactRenderer.copy: no body text for type=${artifact.type} id=${artifact.id} — bubbling`
    )
    return false
  }
  // navigator.clipboard is typed as always-present in lib.dom.d.ts but is
  // genuinely missing in some non-secure-context / jsdom environments —
  // hence the runtime guard. Cast to a nullable view so eslint can see the
  // branch isn't dead.
  const clipboard = (navigator as { clipboard?: Clipboard }).clipboard
  if (typeof navigator === "undefined" || !clipboard) {
    logger.warn("ArtifactRenderer.copy: navigator.clipboard unavailable")
    return false
  }
  try {
    await clipboard.writeText(text)
    return true
  } catch (err) {
    logger.warn(`ArtifactRenderer.copy: writeText failed (${String(err)})`)
    return false
  }
}

function performDownload(artifact: Artifact): boolean {
  const suggestion = suggestDownload(artifact)
  if (!suggestion) {
    logger.warn(
      `ArtifactRenderer.download: no inline body for type=${artifact.type} id=${artifact.id} — bubbling`
    )
    return false
  }
  if (typeof document === "undefined" || typeof URL === "undefined") return false
  try {
    const blob = new Blob([suggestion.body], { type: suggestion.mime })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = suggestion.filename
    link.rel = "noopener"
    // Append + click + remove keeps Firefox happy; some browsers ignore
    // synthesised clicks on detached anchors.
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    // Revoke async — the click may still be reading the URL when this fires
    // synchronously; one tick is enough to keep the download alive.
    setTimeout(() => URL.revokeObjectURL(url), 0)
    return true
  } catch (err) {
    logger.warn(`ArtifactRenderer.download: ${String(err)}`)
    return false
  }
}

// ── Dispatcher ──────────────────────────────────────────────────────────────

export function ArtifactRenderer({
  artifact,
  versionChain,
  onAction,
  onSelectVersion,
  onViewTrace,
  onShowHistory,
  citationSection,
  sessionType,
}: ArtifactRendererProps) {
  const { t } = useTranslation()
  const handleAction = useCallback(
    (
      actionId: string,
      target: Artifact,
      extra?: {
        feedback?: string
        answers?: Record<string, string | string[]>
      }
    ) => {
      if (actionId === "copy") {
        void performCopy(target).then((ok) => {
          if (ok) {
            toast.success(t("deepExplorer.artifact_copied"))
            return
          }
          toast.error(t("deepExplorer.artifact_copy_failed"))
          onAction?.(actionId, target, extra)
        })
        return
      }
      if (actionId === "download") {
        const handled = performDownload(target)
        if (!handled) onAction?.(actionId, target, extra)
        return
      }
      onAction?.(actionId, target, extra)
    },
    [onAction, t]
  )

  const sharedProps = {
    versionChain,
    onAction: handleAction,
    onSelectVersion,
    onViewTrace,
    onShowHistory,
    citationSection,
  }

  switch (artifact.type) {
    case "document":
      return (
        <DocumentRenderer
          {...sharedProps}
          artifact={artifact as Artifact & { payload: DocumentPayload }}
        />
      )
    case "short_answer":
      return (
        <ShortAnswerRenderer
          {...sharedProps}
          artifact={artifact as Artifact & { payload: ShortAnswerPayload }}
        />
      )
    case "email_draft":
      return <EmailDraftRenderer {...sharedProps} artifact={artifact} />
    case "table":
    case "list":
      return <TableRenderer {...sharedProps} artifact={artifact} />
    case "image":
      return (
        <ImageRenderer
          {...sharedProps}
          artifact={artifact as Artifact & { payload: ImagePayload }}
        />
      )
    case "diagram":
      return (
        <DiagramRenderer
          {...sharedProps}
          artifact={artifact as Artifact & { payload: DiagramPayload }}
        />
      )
    case "docx":
    case "xlsx":
    case "pptx":
    case "pdf":
      return (
        <OfficeFileRenderer
          {...sharedProps}
          artifact={artifact as Artifact & { payload: OfficeFilePayload }}
        />
      )
    case "action_receipt":
      return (
        <ActionReceiptRenderer
          {...sharedProps}
          artifact={artifact as Artifact & { payload: ActionReceiptPayload }}
        />
      )
    case "pending_action":
      return (
        <PendingActionRenderer
          {...sharedProps}
          artifact={artifact as Artifact & { payload: PendingActionPayload }}
        />
      )
    case "plan":
      return (
        <PlanRenderer
          {...sharedProps}
          artifact={artifact as Artifact & { payload: PlanPayload }}
          sessionType={sessionType}
        />
      )
    case "ask_user":
      return (
        <AskUserRenderer
          {...sharedProps}
          artifact={artifact as Artifact & { payload: AskUserPayload }}
          sessionType={sessionType}
        />
      )
    // `unknown` and any unrecognised future type share the fallback renderer.
    case "unknown":
    default:
      return (
        <UnknownArtifactRenderer
          {...sharedProps}
          artifact={artifact as Artifact & { payload: UnknownPayload }}
        />
      )
  }
}
